import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Reload } from '@travel/icons/ui';
import { MOBILE_SCREEN, useDeviceType } from '@travel/traveler-core/hooks';
import { toSEOSearchString } from '@travel/traveler-core/utils';
import { FlatButton } from '@travel/ui';
import { cx, isNotEmptyArray } from '@travel/utils';

import { FIRST_PAGE_PARAMS } from 'pages/ProviderListPage/resolver';

import { pushLocation } from 'store/__router/actions';
import { getHeightIfSmartBannerPresent } from 'store/banner/selectors';
import { getLimitationsForCurrentMarket } from 'store/markets/selectors';
import { getAreaLocation, getIsFetchingList, getItems } from 'store/providerList/selectors';

import { EMPTY_ARRAY } from 'constants/defaultValue';
import { Translate } from 'core/translate';
import paths from 'core/universalRouter/paths';
import { ProviderItem, SearchFormQuery } from 'ProviderList-Types';
import { getGoogleMapURL } from 'utils/googleMapURL';

import MapPin from './MapPin';
import SearchedSpotMapPin from './MapPin/SearchedSpotMapPin';
import useMapOffsetYPosition from './useMapOffsetYPosition';

import styles from './map.module.scss';

const SEARCH_FORM_HEIGHT = 116;
const MARGIN_M = 16;

type Props = {
  className?: string;
  /** Object of current page params */
  params: { pageIdentifier: string };
  /** Object of current page query */
  query: SearchFormQuery;
  /** Function to initialized pin and card on map */
  initializePinAndCard?: () => void;
  /** Function to store each map pin in parent layer */
  storeMapPinInListAndMap?: (id: string, mapPin: any) => void;
  /** Callback to be called when map pin item is clicked */
  onMapPinClick?: (item: ProviderItem) => void;
  /** Callback to be called when searched spot map pin item is clicked */
  onSearchedSpotMapPinClick?: (itemId: string, shouldNotResetScroll?: boolean) => void;
  /** Function to set the instance when Map is initialized */
  setMapInstance: (map: google.maps.Map) => void;
  /** String of current currency code */
  currency: string;
  /** String of displayed language */
  language: string;
  /** Flag to define whether the domestic currency sign will be displayed or not */
  isGlobalCurrency?: boolean;
  /** Callback to be called when map pins need to be reset */
  onResetMapPins?: () => void;
  /** Reset selected map pin by id */
  resetMapPin?: (id: string) => void;
  /** Map is in a dialog */
  isInDialog?: boolean;
};

type AreaLocation = {
  lat: number;
  lng: number;
};

function Map(props: Props) {
  const {
    currency,
    onSearchedSpotMapPinClick,
    setMapInstance,
    initializePinAndCard,
    isInDialog,
  } = props;

  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [showSearchAreaButton, setShowSearchAreaButton] = useState(false);
  const currencyRef = useRef(currency);
  const isInitializedMap = useRef(false);

  const offsetY = useMapOffsetYPosition();
  const dispatch = useDispatch();
  const isSP = useDeviceType() === MOBILE_SCREEN;
  const bannerHeight = useSelector(getHeightIfSmartBannerPresent);
  const isFetchingList = useSelector(getIsFetchingList);
  const areaLocation = useSelector(getAreaLocation);
  const { providers, areaName, type } = useSelector(getItems);

  const providerIds = useMemo(() => providers?.map(provider => provider.id).join(), [providers]);
  const providersRef = useRef(providerIds);

  const [zoomLevel, setZoomLevel] = useState(areaLocation.zoomLevel);
  const prevZoomLevel = useRef(areaLocation.zoomLevel);
  const isZoomChangedByAreaRef = useRef(false);

  const areaLocationState = {
    lat: areaLocation.latitude,
    lng: areaLocation.longitude,
  };
  const [location, setLocation] = useState<AreaLocation>(areaLocationState);
  const prevLocation = useRef<AreaLocation>(areaLocationState);

  const MAP_CONTAINER = 'mapContainer';
  const GOOGLE_MAP_URL = getGoogleMapURL(props.language);
  const limitations = useSelector(getLimitationsForCurrentMarket);

  // to update when initial location update
  useEffect(() => {
    if (
      areaLocation.latitude !== prevLocation.current.lat ||
      areaLocation.longitude !== prevLocation.current.lng
    ) {
      const newLocation = {
        lat: areaLocation.latitude,
        lng: areaLocation.longitude,
      };

      prevLocation.current = newLocation;
      map?.panTo(newLocation);
      setLocation(newLocation);
    }
  }, [areaLocation.latitude, areaLocation.longitude, map]);

  // to update when initial zoom level update
  useEffect(() => {
    if (areaLocation.zoomLevel !== prevZoomLevel.current) {
      prevZoomLevel.current = areaLocation.zoomLevel;
      isZoomChangedByAreaRef.current = true;
      map?.setZoom(areaLocation.zoomLevel);
      setZoomLevel(areaLocation.zoomLevel);
    }
  }, [areaLocation.zoomLevel, map]);

  // to initialize the map
  const handleLoaded = useCallback(async () => {
    const options = {
      center: { lat: areaLocation.latitude, lng: areaLocation.longitude },
      zoom: areaLocation.zoomLevel,
      fullscreenControl: false,
      streetViewControl: false,
      mapTypeControl: false,
      clickableIcons: false,
    };

    const getMapDiv = () =>
      new Promise<HTMLElement>(resolve => {
        let mapDiv = document.getElementById(MAP_CONTAINER);
        const getTimer = setInterval(() => {
          mapDiv = document.getElementById(MAP_CONTAINER);
          if (mapDiv) {
            clearInterval(getTimer);
            resolve(mapDiv);
          }
        }, 100);
      });

    let mapDiv = document.getElementById(MAP_CONTAINER);
    if (!mapDiv && isInDialog) {
      mapDiv = await getMapDiv();
    }
    const instance = new window.google.maps.Map(mapDiv, options);

    if (instance) {
      instance.addListener('click', () => {
        // to reset selected state
        initializePinAndCard?.();
      });

      instance.addListener('zoom_changed', () => {
        setZoomLevel(instance.getZoom());

        if (!isZoomChangedByAreaRef.current) {
          setShowSearchAreaButton(true);
        }

        // reset value on zoom changed
        isZoomChangedByAreaRef.current = false;
      });

      instance.addListener('dragend', () => {
        setLocation({
          lat: instance.getCenter().lat(),
          lng: instance.getCenter().lng(),
        });
        setShowSearchAreaButton(true);
      });

      setMap(instance);
      // from parent
      setMapInstance(instance);
      isInitializedMap.current = true;
    }
  }, [
    areaLocation.latitude,
    areaLocation.longitude,
    areaLocation.zoomLevel,
    initializePinAndCard,
    isInDialog,
    setMapInstance,
  ]);

  useEffect(() => {
    // Don't load scriptjs as a dependency since we do not want this module be used on server side.
    const scriptjs = require('scriptjs');
    if (!window.google) {
      scriptjs(GOOGLE_MAP_URL, handleLoaded);
    } else {
      if (
        !isInitializedMap.current ||
        currencyRef.current !== currency ||
        providersRef.current !== providerIds
      ) {
        // NOTE: update on currency/provider list change to clear all markers ref
        currencyRef.current = currency;
        providersRef.current = providerIds;
        handleLoaded().then(() => {
          if (areaName && type === 'SPOT') {
            onSearchedSpotMapPinClick?.(areaName, true);
          }
        });
      }
    }
  }, [
    GOOGLE_MAP_URL,
    areaName,
    handleLoaded,
    currency,
    onSearchedSpotMapPinClick,
    providerIds,
    type,
  ]);

  const searchThisArea = () => {
    if (isFetchingList) {
      return null;
    }

    const listProps = {
      params: props.params,
      query: {
        ...props.query,
        lat: location.lat,
        lng: location.lng,
        zoomLevel: zoomLevel,
        // reset page state
        ...FIRST_PAGE_PARAMS,
      },
    };

    dispatch(
      pushLocation({
        pathname: paths.providerList.pathResolver(props.params.pageIdentifier),
        search: toSEOSearchString(listProps.query, limitations),
        ...(isInDialog ? { state: { isInDialog: true } } : {}),
      }),
    );
  };

  // NOTE: only render pins which has location and price information on Map
  const pinItems = useMemo(
    () =>
      isNotEmptyArray(providers)
        ? providers.filter(item => item.location && item.price?.total)
        : EMPTY_ARRAY,
    [providers],
  );

  return (
    <div className={cx(styles.wrapper, props.className)} data-testid="map-wrapper">
      {showSearchAreaButton && (
        <FlatButton
          data-testid="map-searchThisArea-button"
          className={styles.searchThisArea}
          icon={<Reload size={16} />}
          classType="list"
          onClick={searchThisArea}
          style={isSP ? { top: bannerHeight + SEARCH_FORM_HEIGHT + MARGIN_M } : undefined}
        >
          <Translate id="Provider_List.Map_View.Search_Area" />
        </FlatButton>
      )}

      <div
        className={styles.mapContainer}
        id={MAP_CONTAINER}
        style={{ transform: `translateY(${offsetY}px)` }}
      >
        {map && isNotEmptyArray(providers)
          ? pinItems.map((item: ProviderItem, index) => (
              <MapPin
                key={item.id}
                item={item}
                map={map}
                storeMapPinInListAndMap={props.storeMapPinInListAndMap}
                onMapPinClick={props.onMapPinClick}
                zIndex={providers.length - index}
                language={props.language}
                currency={currency}
                isGlobalCurrency={props.isGlobalCurrency}
                resetMapPin={props.resetMapPin}
              />
            ))
          : null}
        {map && areaName && type === 'SPOT' ? (
          <SearchedSpotMapPin
            map={map}
            zIndex={providers.length + 1}
            areaName={areaName}
            areaLocation={areaLocationState}
            storeMapPinInListAndMap={props.storeMapPinInListAndMap}
            onSearchedSpotMapPinClick={onSearchedSpotMapPinClick}
            resetMapPin={props.resetMapPin}
          />
        ) : null}
      </div>
    </div>
  );
}

export default Map;
